import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { api } from './api';

// Global debug logger (will be set by App.tsx)
let debugLogger: ((message: string, type?: 'info' | 'success' | 'warning' | 'error') => void) | null = null;

export const setDebugLogger = (logger: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void) => {
    debugLogger = logger;
};

const log = (message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    console.log(message);
    if (debugLogger) {
        debugLogger(message, type);
    }
};

/**
 * Función helper para despertar la app en Android
 * Requiere: cordova-plugin-background-mode
 */
const wakeUpApp = () => {
    try {
        const bgMode = (window as any).cordova?.plugins?.backgroundMode;
        if (bgMode) {
            log('🔔 [WAKE-UP] Intentando despertar la pantalla...', 'warning');

            // 1. Habilitar modo background si no lo estaba
            bgMode.enable();

            // 2. Despertar pantalla (turn screen on)
            bgMode.wakeUp();

            // 3. Desbloquear (si no tiene seguridad alta)
            bgMode.unlock();

            // 4. Traer app al frente (clave para "Pantalla Completa")
            bgMode.moveToForeground();

            log('✅ [WAKE-UP] Comandos de despertado enviados (WakeUp+Unlock+Foreground)', 'success');
        } else {
            log('⚠️ [WAKE-UP] Plugin background-mode no detectado. No se puede despertar la app.', 'warning');
        }
    } catch (e: any) {
        log(`❌ [WAKE-UP] Error al intentar despertar: ${e.message}`, 'error');
    }
};

let listenersRegistered = false;

export const initPushNotifications = async (userId?: string) => {
    log('🚀 [FCM] Iniciando Push Notifications (con Wake-Up)...', 'info');
    log(`🚀 [FCM] User ID: ${userId}`, 'info');
    log(`🚀 [FCM] Platform: ${Capacitor.getPlatform()}`, 'info');
    log(`🚀 [FCM] Is Native?: ${Capacitor.isNativePlatform()}`, 'info');

    // Solo inicializar en plataformas nativas (Android/iOS)
    if (!Capacitor.isNativePlatform()) {
        log('⚠️ [FCM] Push notifications skipped: Not a native platform', 'warning');
        return;
    }

    try {
        // Inicializar Background Mode si existe
        const bgMode = (window as any).cordova?.plugins?.backgroundMode;
        if (bgMode) {
            bgMode.enable();
            // Configurar para que las notificaciones de background no sean molestas (opcional)
            bgMode.setDefaults({
                title: "Amigo Peludo Activo",
                text: "Monitoreando alertas...",
                silent: true
            });
            log('✅ [BG-MODE] Plugin inicializado y activo', 'success');
        }

        log('📝 [FCM] Step 1: Requesting permissions...', 'info');
        // 1. Request Permission
        const result = await FirebaseMessaging.requestPermissions();
        log(`📝 [FCM] Permission result: ${JSON.stringify(result)}`, 'info');

        if (result.receive !== 'granted') {
            log(`❌ [FCM] Permission NOT granted: ${result.receive}`, 'error');
            return;
        }

        log('✅ [FCM] Permission granted!', 'success');

        log('📝 [FCM] Step 2: Getting FCM Token...', 'info');
        // 2. Get FCM Token
        const { token } = await FirebaseMessaging.getToken();
        log(`🔥 [FCM] Firebase Push Token: ${token}`, 'success');

        // 3. Save to Backend
        if (userId && token) {
            log('📝 [FCM] Step 3: Saving token to backend...', 'info');
            log(`📝 [FCM] User ID: ${userId}`, 'info');
            log(`📝 [FCM] Token (first 20 chars): ${token.substring(0, 20)}...`, 'info');

            const response = await api.savePushToken(userId, token);
            log(`✅ [FCM] Backend response: ${JSON.stringify(response)}`, 'success');
        } else {
            log('⚠️ [FCM] Skipping backend save - Missing userId or token', 'warning');
            log(`⚠️ [FCM] userId: ${userId}`, 'warning');
            log(`⚠️ [FCM] token exists: ${!!token}`, 'warning');
        }

        if (listenersRegistered) {
            log('ℹ️ [FCM] Listeners ya registrados en esta sesión — se omite duplicar.', 'info');
            log('✅ [FCM] Initialization complete!', 'success');
            return;
        }
        listenersRegistered = true;

        log('📝 [FCM] Step 4: Adding notification listeners...', 'info');

        // 4. Add Listeners
        await FirebaseMessaging.addListener('notificationReceived', (event) => {
            log(`📬 [FCM] Push received: ${JSON.stringify(event)}`, 'info');

            // ANALIZAR SI ES PRIORIDAD ALTA PARA ACTIVAR PANTALLA
            const data = event.notification.data;
            if (data && (data.priority === 'high' || data.type === 'system_alert' || data.level === '5' || data.level === '2')) {
                wakeUpApp();
            }

            // Dispatch internal event for UI to react (e.g. SystemAlertOverlay)
            const customEvent = new CustomEvent('system-alert', { detail: event.notification.data });
            window.dispatchEvent(customEvent);
        });

        await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
            log(`👆 [FCM] Push tapped: ${JSON.stringify(event)}`, 'info');
            // Al tocar la notificación, aseguramos traer al frente también
            wakeUpApp();

            // Genérico: cualquier push que traiga "view" en su data abre esa
            // pantalla al tocarlo, pasando también el resto de los datos (id,
            // petId, lat/lng, pet_name, etc.) tal cual App.tsx ya sabe leerlos.
            // Antes cada tipo de notificación necesitaba su propio "if" acá
            // (solo estaban cableados a mano "solicitud de adopción" y "te
            // etiquetaron"), así que otros pushes que el backend ya manda con
            // "view" (por ejemplo los de GPS_TRACKING al escanear el QR o
            // salir de la geocerca) llegaban al teléfono pero tocarlos no
            // abría nada — con esto quedan resueltos también, sin agregar
            // código nuevo por cada caso.
            const data = (event as any)?.notification?.data || {};
            if (data.view) {
                window.dispatchEvent(new CustomEvent('change-view', { detail: { ...data } }));
            }
        });

        log('✅ [FCM] Initialization complete!', 'success');

    } catch (e: any) {
        log(`❌ [FCM] Error during initialization: ${e.message}`, 'error');
        log(`❌ [FCM] Error details: ${JSON.stringify({ name: e.name, message: e.message })}`, 'error');
    }
};
